import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { Home, BookOpen, BarChart2, Target, ListChecks, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "./i18n";
import {
  collection, doc, onSnapshot,
  orderBy, query, setDoc, updateDoc, where,
} from "firebase/firestore";
import {
  GoogleAuthProvider, User, onAuthStateChanged, signInWithPopup, signOut,
  signInWithEmailAndPassword, sendPasswordResetEmail,
  sendEmailVerification, signInAnonymously, linkWithPopup, linkWithCredential,
  EmailAuthProvider, browserPopupRedirectResolver,
} from "firebase/auth";
import { auth, db, provider } from "./app/firebase";
import { registerWebMCPTool } from "./app/webmcp";
import { formatBabyAge } from "./shared/lib/date";
import { getNormForWeek, richNorms } from "./data/norms";
import { VACCINATION_DATA, getAllScheduleWeeks } from "./data/vaxSchedules";
import { calculateAgeWeeks, toDateKey, localDatetimeString } from "./shared/lib/date";
import { calculateGrowthVelocity, calculateStatus } from "./shared/lib/insights";
import type {
  ActiveSleepSession, AiResponse, BabyProfile, DailyStats, DevelopmentalLeapResult, DevelopmentalNorm, LogEvent, LogType,
  MilestoneAchievement, Task, UnitSystem, UserSettings,
} from "./shared/types/domain";
import { buildCopilotContext, detectDevelopmentalLeap } from "./shared/lib/copilot";
import { httpsCallable } from "firebase/functions";
import { functions } from "./app/firebase";
import { SUPPORTED_LANGUAGES, WEBMCP_LOG_TYPES } from "./shared/types/domain";
import { initialStats } from "./shared/lib/format";
import { buildAppUrl, parseInitialViewFromPath, type View } from "./shared/lib/appRouting";
import { reportSignupConversion, reportLogActivityClick, reportAccountSignup } from "./shared/lib/analytics";
import { getStoredAcquisitionParams } from "./shared/lib/acquisition";
import { ignoreTransientSnapshotError, subscribeWithPermissionRetry } from "./shared/lib/firestoreSubscribe";
import { getAuthErrorMessage } from "./shared/lib/authErrors";
import { detectCountryFromBrowser, unitSystemForCountry } from "./shared/lib/locale";
import { deleteLogEvent } from "./shared/lib/logMutations";
import { HomeView } from "./views/HomeView";
import { SleepTimerBanner } from "./components/SleepTimerBanner";
import { ReferenceModal } from "./components/ReferenceModal";
import { RegisterScreen } from "./views/auth/RegisterScreen";
import { SignInScreen } from "./views/auth/SignInScreen";
import { OnboardingView } from "./views/OnboardingView";

// Everything below is only needed once the user navigates past the default Home tab (or opens
// the log form), so it's code-split out of the initial bundle rather than loaded eagerly.
const LoggingView = lazy(() => import("./views/LoggingView").then((m) => ({ default: m.LoggingView })));
const AnalyticsView = lazy(() => import("./views/AnalyticsView").then((m) => ({ default: m.AnalyticsView })));
const MilestonesView = lazy(() => import("./views/MilestonesView").then((m) => ({ default: m.MilestonesView })));
const TasksView = lazy(() => import("./views/TasksView").then((m) => ({ default: m.TasksView })));
const ImportView = lazy(() => import("./views/ImportView").then((m) => ({ default: m.ImportView })));
const SettingsView = lazy(() => import("./views/SettingsView").then((m) => ({ default: m.SettingsView })));
const FormOverlay = lazy(() => import("./views/FormOverlay").then((m) => ({ default: m.FormOverlay })));
const AboutView = lazy(() => import("./views/AboutView").then((m) => ({ default: m.AboutView })));

function ViewLoadingFallback() {
  const { t } = useTranslation();
  return (
    <div className="view view-loading" role="status" aria-live="polite" aria-label={t("common.loading")}>
      <div className="spinner" />
    </div>
  );
}

interface CreateBabyProfileInput {
  name?: string;
  birthDate?: string;
  sex?: "boy" | "girl";
  feedingMode?: "bottle" | "nursing" | "both";
  countryCode?: BabyProfile["countryCode"];
  unitSystem?: UnitSystem;
  language?: string;
}

export function App() {
  const { t } = useTranslation();

  // ── Auth & global data ──────────────────────────────────────────────────────
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authInitError, setAuthInitError] = useState(false);
  const [authScreen, setAuthScreen] = useState<"none" | "register" | "signin">(() => {
    const hash = window.location.hash.slice(1);
    return hash === "signin" || hash === "register" ? hash : "none";
  });
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [verifyResent, setVerifyResent] = useState(false);
  const [verifyChecking, setVerifyChecking] = useState(false);
  const [verifyStillUnverified, setVerifyStillUnverified] = useState(false);
  const [babies, setBabies] = useState<BabyProfile[]>([]);
  const [activeBaby, setActiveBaby] = useState<BabyProfile | null>(null);
  const [babiesReady, setBabiesReady] = useState(false);
  const [view, setView] = useState<View>(() => parseInitialViewFromPath(window.location.pathname));
  const [logSubTab, setLogSubTab] = useState("all");
  const [logs, setLogs] = useState<LogEvent[]>([]);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const [vaccineLogs, setVaccineLogs] = useState<LogEvent[]>([]);
  const [stats, setStats] = useState<DailyStats[]>([]);
  const [norm, setNorm] = useState<DevelopmentalNorm | null>(null);
  const [milestoneAchievements, setMilestoneAchievements] = useState<MilestoneAchievement[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [userSettings, setUserSettings] = useState<UserSettings>({ unitSystem: "metric", language: "en", aiEnabled: false });
  const [selectedLog, setSelectedLog] = useState<LogEvent | null>(null);
  const [aiResponse, setAiResponse] = useState<AiResponse | null>(null);
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [copilotGeneratedAt, setCopilotGeneratedAt] = useState<string | null>(null);

  // ── UI overlay state ────────────────────────────────────────────────────────
  const [formOverlayOpen, setFormOverlayOpen] = useState(false);
  const [formOverlayAutoType, setFormOverlayAutoType] = useState<LogType | undefined>(undefined);
  const [referenceModal, setReferenceModal] = useState<"feeding" | "diapers" | "growth" | "sleepPrediction" | null>(null);
  const [activeSleepSession, setActiveSleepSession] = useState<ActiveSleepSession | null>(null);

  // ── Onboarding state (only used before activeBaby exists) ───────────────────
  const [newBabyName, setNewBabyName] = useState("");
  const [newBabyBirthDate, setNewBabyBirthDate] = useState("");
  const [newBabySex, setNewBabySex] = useState<"boy" | "girl" | "">("");
  const [newBabyFeedingMode, setNewBabyFeedingMode] = useState<"bottle" | "nursing" | "both">("bottle");
  const [newBabyCountryCode, setNewBabyCountryCode] = useState(() => detectCountryFromBrowser());
  const [onboardingSubmitAttempted, setOnboardingSubmitAttempted] = useState(false);

  // ── Firestore subscriptions ─────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (nextUser) => {
      if (!nextUser) {
        signInAnonymously(auth).catch(() => {
          setAuthInitError(true);
          setLoading(false);
        });
        return;
      }
      setUser(nextUser);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const userEmail = user?.email;
  const userUid = user?.uid;
  // Firestore's `canReadBabyDoc()` rule only grants the collaborator branch
  // when the auth token has a verified email; querying by collaboratorEmails
  // before that (e.g. right after linking an anonymous account) makes the
  // rule engine statically deny the whole list request. Owned babies are
  // always queried by ownerUid, which needs no verification, and the
  // collaborator query is only added once the email is actually verified.
  const userEmailVerified = user?.emailVerified;

  useEffect(() => {
    if (!userUid) return;
    const ownedById = new Map<string, BabyProfile>();
    const sharedById = new Map<string, BabyProfile>();
    const publish = () => {
      const next = [...ownedById.values(), ...sharedById.values()].filter(
        (baby, index, all) => all.findIndex((b) => b.id === baby.id) === index
      );
      setBabies(next);
      setBabiesReady(true);
      setActiveBaby((prev) => {
        if (!prev) return prev;
        const updated = next.find((b) => b.id === prev.id);
        return updated ?? null;
      });
    };

    const ownedQuery = query(collection(db, "babies"), where("ownerUid", "==", userUid));
    const unsubOwned = onSnapshot(ownedQuery, (snapshot) => {
      ownedById.clear();
      for (const item of snapshot.docs) {
        ownedById.set(item.id, { id: item.id, ...item.data() } as BabyProfile);
      }
      publish();
    }, ignoreTransientSnapshotError);

    let unsubShared = () => {};
    if (userEmailVerified && userEmail) {
      const sharedQuery = query(
        collection(db, "babies"),
        where("collaboratorEmails", "array-contains", userEmail.toLowerCase())
      );
      unsubShared = onSnapshot(sharedQuery, (snapshot) => {
        sharedById.clear();
        for (const item of snapshot.docs) {
          sharedById.set(item.id, { id: item.id, ...item.data() } as BabyProfile);
        }
        publish();
      }, ignoreTransientSnapshotError);
    }

    return () => { unsubOwned(); unsubShared(); };
  }, [userEmail, userEmailVerified, userUid]);

  useEffect(() => {
    if (!user?.uid) return;
    const userRef = doc(db, "users", user.uid);
    const unsub = onSnapshot(userRef, (snap) => {
      if (snap.exists()) {
        setUserSettings(snap.data() as UserSettings);
      } else {
        const supported = ["en", "de", "es", "tr", "hi", "ar", "it", "ru"];
        const lang = navigator.language.slice(0, 2).toLowerCase();
        const initial: UserSettings = {
          unitSystem: unitSystemForCountry(detectCountryFromBrowser()),
          language: supported.includes(lang) ? lang : "en",
          aiEnabled: false,
        };
        setDoc(userRef, initial);
        setUserSettings(initial);
      }
    }, ignoreTransientSnapshotError);
    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
    if (userSettings.language) i18n.changeLanguage(userSettings.language);
  }, [userSettings.language]);

  useEffect(() => {
    if (!activeBaby?.id) return;
    setLogs([]);
    setLogsLoaded(false);
    setVaccineLogs([]);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const logsQuery = query(
      collection(db, "babies", activeBaby.id, "logEvents"),
      where("occurredAt", ">=", thirtyDaysAgo.toISOString()),
      orderBy("occurredAt", "desc")
    );
    const statsQuery = query(
      collection(db, "babies", activeBaby.id, "dailyStats"),
      orderBy("date", "desc")
    );
    const tasksQuery = query(
      collection(db, "babies", activeBaby.id, "tasks"),
      where("isActive", "==", true)
    );
    // Vaccination records need to stay visible for the whole schedule (up to a year+),
    // so they're queried separately from the 30-day-windowed logsQuery above.
    const vaccineLogsQuery = query(
      collection(db, "babies", activeBaby.id, "logEvents"),
      where("type", "==", "vaccine")
    );
    const unsubLogs = subscribeWithPermissionRetry(logsQuery, (snapshot) => {
      const docs = snapshot.docs.map((item) => ({ id: item.id, babyId: activeBaby.id, ...item.data() }) as LogEvent);
      docs.sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
      setLogs(docs);
      setLogsLoaded(true);
    });
    const unsubVaccineLogs = subscribeWithPermissionRetry(vaccineLogsQuery, (snapshot) => {
      const docs = snapshot.docs.map((item) => ({ id: item.id, babyId: activeBaby.id, ...item.data() }) as LogEvent);
      setVaccineLogs(docs);
    });
    const unsubStats = subscribeWithPermissionRetry(statsQuery, (snapshot) => {
      setStats(snapshot.docs.map((item) => item.data() as DailyStats));
    });
    const unsubMilestones = subscribeWithPermissionRetry(
      collection(db, "babies", activeBaby.id, "milestones"),
      (snapshot) => {
        setMilestoneAchievements(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }) as MilestoneAchievement));
      }
    );
    const unsubTasks = subscribeWithPermissionRetry(tasksQuery, (snapshot) => {
      setTasks(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }) as Task));
    });
    return () => { unsubLogs(); unsubVaccineLogs(); unsubStats(); unsubMilestones(); unsubTasks(); };
  }, [activeBaby?.id]);

  useEffect(() => {
    if (!activeBaby?.id) { setActiveSleepSession(null); return; }
    const sessionRef = doc(db, "babies", activeBaby.id, "activeSleepSession", "current");
    const unsub = onSnapshot(sessionRef, (snap) => {
      setActiveSleepSession(snap.exists() ? (snap.data() as ActiveSleepSession) : null);
    }, ignoreTransientSnapshotError);
    return () => unsub();
  }, [activeBaby?.id]);

  useEffect(() => {
    if (!activeBaby) return;
    const ageWeeks = calculateAgeWeeks(activeBaby.birthDate);
    const normDoc = doc(db, "developmentalNorms", String(ageWeeks));
    const unsub = onSnapshot(normDoc, (snapshot) => {
      setNorm(snapshot.exists() ? (snapshot.data() as DevelopmentalNorm) : getNormForWeek(ageWeeks));
    }, ignoreTransientSnapshotError);
    return () => unsub();
  }, [activeBaby]);

  // ── Browser history sync ────────────────────────────────────────────────────
  // Tab/screen switches, the Logs sub-tabs, and the log-entry overlay used to
  // be invisible to the browser's session history (only a cosmetic
  // replaceState of the hash), so pressing Back skipped over them entirely
  // and left the app. `view`/`authScreen`/`logSubTab` changes now push a real
  // history entry (so Back undoes one switch at a time); the overlay pushes
  // its own entry on open and is closed by history.back() so repeated
  // open/close cycles don't pile up extra entries.
  const viewHistoryInitializedRef = useRef(false);
  const formOverlayPushedRef = useRef(false);
  // Tracks the {view, authScreen, logSubTab} triple last written to
  // `history` — by either side of the sync. If the push-effect below fires
  // and finds these already match, the change came from a popstate we
  // already recorded, not fresh in-app navigation, so it shouldn't push again.
  // (A boolean "just came from popstate" flag can't work here: when popstate
  // sets state to values that are already current — e.g. closing the overlay
  // via history.back() after it was already closed by the X button — React
  // bails out of re-rendering, so an effect-based reset of that flag would
  // never run and it'd stay stuck, silently swallowing the next real push.)
  const lastHistoryStateRef = useRef<{ view: View; authScreen: typeof authScreen; logSubTab: string } | null>(null);

  useEffect(() => {
    function handlePopState(event: PopStateEvent) {
      const state = event.state as
        | {
            view?: View;
            authScreen?: "none" | "register" | "signin";
            logSubTab?: string;
            formOverlayOpen?: boolean;
          }
        | null;
      const nextView = state?.view ?? "home";
      const nextAuthScreen = state?.authScreen ?? "none";
      const nextLogSubTab = state?.logSubTab ?? "all";
      lastHistoryStateRef.current = { view: nextView, authScreen: nextAuthScreen, logSubTab: nextLogSubTab };
      const shouldFormBeOpen = !!state?.formOverlayOpen;
      formOverlayPushedRef.current = shouldFormBeOpen;
      setFormOverlayOpen(shouldFormBeOpen);
      if (!shouldFormBeOpen) setFormOverlayAutoType(undefined);
      setAuthScreen(nextAuthScreen);
      setView(nextView);
      setLogSubTab(nextLogSubTab);
    }
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    // Don't touch the URL until we actually know which of the three states (loading,
    // signed-in-with-no-baby-yet, main app) we've settled into — otherwise the very
    // first run fires while `activeBaby` is still resolving, replaces whatever real
    // URL the tab loaded with (e.g. a bookmarked /mybaby/analytics) with "/", and the
    // correction a moment later burns a pushState instead of a replaceState, leaving
    // an extra "/" entry for Back to land on. This mirrors the loading-guard render
    // branch below.
    if (loading || (!!user && (!babiesReady || (!activeBaby && babies.length > 0)))) return;

    const isMainAppView = !!user && authScreen === "none" && !!activeBaby;
    // Guard above already resolved the loading/localStorage-restore race, so !activeBaby here
    // means "genuinely no baby yet" (the OnboardingView branch), not "still figuring it out".
    const showOnboarding = !!user && authScreen === "none" && !activeBaby;
    const url = buildAppUrl({ authScreen, view, isMainAppView, showOnboarding });
    const state = { view, authScreen, logSubTab };

    const last = lastHistoryStateRef.current;
    if (last && last.view === view && last.authScreen === authScreen && last.logSubTab === logSubTab) {
      return;
    }
    lastHistoryStateRef.current = { view, authScreen, logSubTab };

    if (!viewHistoryInitializedRef.current) {
      viewHistoryInitializedRef.current = true;
      history.replaceState(state, "", url);
      return;
    }
    history.pushState(state, "", url);
  }, [authScreen, view, logSubTab, loading, user, babiesReady, activeBaby, babies]);

  function openFormOverlay(autoType?: LogType) {
    formOverlayPushedRef.current = true;
    history.pushState({ ...history.state, formOverlayOpen: true }, "");
    setFormOverlayAutoType(autoType);
    setFormOverlayOpen(true);
  }

  function closeFormOverlay() {
    if (formOverlayPushedRef.current) {
      formOverlayPushedRef.current = false;
      history.back();
    }
    setFormOverlayOpen(false);
    setFormOverlayAutoType(undefined);
  }

  // Subscribe to AI response cached in Firestore — updates automatically when the function writes
  useEffect(() => {
    if (!activeBaby?.id || !userSettings.aiEnabled) {
      setAiResponse(null);
      return;
    }
    setAiResponse(null);

    const currentLang = userSettings.language ?? "en";
    const latestRef = doc(db, "babies", activeBaby.id, "insights", "latest");
    const unsub = onSnapshot(latestRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data() as AiResponse & { language?: string; generatedAt?: { toDate?: () => Date } };
      if (data.language && data.language !== currentLang) return;
      const { language: _lang, generatedAt: ts, ...rest } = data;
      setAiResponse(rest as AiResponse);
      if (ts?.toDate) setCopilotGeneratedAt(ts.toDate().toISOString());
    }, ignoreTransientSnapshotError);
    return () => unsub();
  }, [activeBaby?.id, userSettings.aiEnabled, userSettings.language]);

  // Trigger the Cloud Function on baby switch / app mount only (not on every log change)
  useEffect(() => {
    const MIN_LOGS_FOR_AI = 5;
    if (!activeBaby?.id || !user || logs.length < MIN_LOGS_FOR_AI || !userSettings.aiEnabled) return;

    let cancelled = false;

    const ageWeeks = calculateAgeWeeks(activeBaby.birthDate);
    const ctx = buildCopilotContext(
      activeBaby,
      ageWeeks,
      logs,
      norm,
      milestoneAchievements.map(m => m.milestoneText)
    );

    const callFn = httpsCallable<
      { babyId: string; context: unknown; language: string },
      { aiResponse: AiResponse; fromCache: boolean; generatedAt: string }
    >(functions, "generateBabyInsights");

    setCopilotLoading(true);
    callFn({ babyId: activeBaby.id, context: ctx, language: userSettings.language ?? "en" })
      .then((result) => {
        if (cancelled) return;
        if (result.data.aiResponse) setAiResponse(result.data.aiResponse);
        if (result.data.generatedAt) setCopilotGeneratedAt(result.data.generatedAt);
        setCopilotLoading(false);
      })
      .catch(() => { if (!cancelled) setCopilotLoading(false); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBaby?.id, logs.length > 0, userSettings.aiEnabled, userSettings.language]);

  useEffect(() => {
    if (babiesReady && !activeBaby && babies.length > 0) {
      const savedId = localStorage.getItem("activeBabyId");
      const saved = savedId ? babies.find((b) => b.id === savedId) : null;
      setActiveBaby(saved ?? babies[0]);
    }
  }, [babiesReady, activeBaby, babies]);

  // ── Shared mutations ────────────────────────────────────────────────────────
  function saveUserSettings(patch: Partial<UserSettings>) {
    if (!user?.uid) return;
    const next = { ...userSettings, ...patch };
    setUserSettings(next);
    setDoc(doc(db, "users", user.uid), next, { merge: true });
  }

  async function handleDeleteLog(item: LogEvent) {
    if (!activeBaby) return;
    await deleteLogEvent(activeBaby.id, item);
    setSelectedLog(null);
  }

  // ── Global computed values ──────────────────────────────────────────────────
  const todayKey = toDateKey(localDatetimeString());
  const todaysStats = useMemo(
    () => stats.find((item) => item.date === todayKey) ?? { ...initialStats, date: todayKey },
    [stats, todayKey]
  );
  const markers = useMemo(
    () => logs.filter((item) => item.type === "marker" && item.occurredAt.startsWith(todayKey)),
    [logs, todayKey]
  );
  const growthVelocity = useMemo(() => calculateGrowthVelocity(logs), [logs]);
  const latestWeightKg = useMemo(() => {
    const entry = logs
      .filter((l) => l.type === "weight" && l.payload.weightG)
      .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())[0];
    return entry ? entry.payload.weightG! / 1000 : null;
  }, [logs]);
  const effectiveFeedingMinMl = latestWeightKg !== null ? Math.round(latestWeightKg * 150) : 0;
  const effectiveFeedingTargetMl = latestWeightKg !== null ? Math.round(latestWeightKg * 180) : 0;
  const insight = useMemo(() => {
    if (!norm || !activeBaby) return null;
    return calculateStatus({
      stats: todaysStats,
      norm: { ...norm, feedingMinMl: effectiveFeedingMinMl, feedingTargetMl: effectiveFeedingTargetMl },
      markers,
      growthVelocityGPerWeek: growthVelocity,
      feedingMode: activeBaby.feedingMode,
      tasks,
      vaccineLogs,
      babyAgeWeeks: calculateAgeWeeks(activeBaby.birthDate),
      countryCode: activeBaby.countryCode
    });
  }, [activeBaby, effectiveFeedingMinMl, effectiveFeedingTargetMl, growthVelocity, markers, norm, tasks, todaysStats, vaccineLogs]);

  // ── Onboarding helper ───────────────────────────────────────────────────────
  async function onAddBaby(input?: CreateBabyProfileInput) {
    const name_ = input?.name ?? newBabyName;
    const birthDate_ = input?.birthDate ?? newBabyBirthDate;
    if (!user || !name_ || !birthDate_) return;
    const sex_ = input?.sex ?? newBabySex;
    const feedingMode_ = input?.feedingMode ?? newBabyFeedingMode;
    const countryCode_ = input?.countryCode ?? newBabyCountryCode;
    const { addDoc, serverTimestamp: ts } = await import("firebase/firestore");
    const acquisition = getStoredAcquisitionParams();
    const babyRef = await addDoc(collection(db, "babies"), {
      name: name_,
      birthDate: birthDate_,
      ownerUid: user.uid,
      collaboratorEmails: user.email ? [user.email.toLowerCase()] : [],
      ...(feedingMode_ ? { feedingMode: feedingMode_ } : {}),
      ...(sex_ ? { sex: sex_ } : {}),
      ...(countryCode_ ? { countryCode: countryCode_ } : {}),
      ...(acquisition ? { acquisition } : {}),
      createdAt: ts(),
      updatedAt: ts()
    });
    reportSignupConversion();
    if (input?.unitSystem || input?.language) {
      saveUserSettings({
        ...(input.unitSystem ? { unitSystem: input.unitSystem } : {}),
        ...(input.language ? { language: input.language } : {}),
      });
    }
    const nextWeek9am = new Date();
    nextWeek9am.setDate(nextWeek9am.getDate() + 7);
    nextWeek9am.setHours(9, 0, 0, 0);
    for (const title of [t("tasks.defaultWeightTitle"), t("tasks.defaultHeadCircumferenceTitle")]) {
      await addDoc(collection(db, "babies", babyRef.id, "tasks"), {
        babyId: babyRef.id, title, createdByUid: user.uid,
        isRecurring: true, recurrenceType: "interval",
        intervalConfig: { value: 7, unit: "days" },
        nextDueAt: nextWeek9am.toISOString(), isActive: true,
      });
    }
    setNewBabyName(""); setNewBabyBirthDate(""); setNewBabySex("");
    setNewBabyFeedingMode("bottle"); setNewBabyCountryCode(detectCountryFromBrowser());
  }

  const onAddBabyRef = useRef(onAddBaby);
  useEffect(() => { onAddBabyRef.current = onAddBaby; });

  // Registered only while the onboarding screen (no active baby yet) is what's rendered —
  // SettingsView registers the same tool name for the "already have a baby" case, so the two
  // are mutually exclusive at runtime.
  useEffect(() => {
    if (!user || activeBaby) return;
    const controller = new AbortController();
    registerWebMCPTool({
      name: "create_baby_profile",
      description: "Creates a new baby profile to start tracking. Available before any baby profile exists yet, including for a brand-new, not-yet-signed-in visitor.",
      inputSchema: {
        type: "object",
        properties: {
          name: { type: "string", description: "The baby's name." },
          birthDate: { type: "string", description: "The baby's date of birth, as YYYY-MM-DD." },
          sex: { type: "string", enum: ["boy", "girl"], description: "Optional." },
          feedingMode: { type: "string", enum: ["bottle", "nursing", "both"], description: "Optional, defaults to 'bottle'." },
          countryCode: { type: "string", enum: ["US", "UK", "DE", "TR", "Generic"], description: "Optional. Determines the vaccination schedule shown." },
          unitSystem: { type: "string", enum: ["metric", "imperial"], description: "Optional app-wide preference." },
          language: { type: "string", enum: SUPPORTED_LANGUAGES.map((l) => l.code), description: "Optional app-wide preference, as a language code." },
        },
        required: ["name", "birthDate"],
      },
      annotations: { readOnlyHint: false },
      execute: async (args) => {
        const input = args as CreateBabyProfileInput;
        await onAddBabyRef.current(input);
        return `Created a profile for ${input.name}.`;
      },
    }, controller.signal);
    return () => controller.abort();
  }, [user, activeBaby]);

  // ── Read-only WebMCP tools: shared context about the active baby, available regardless of
  // which tab is currently visible (unlike the write tools above, which are tab-scoped). ──────
  const latestDataRef = useRef({ activeBaby, logs, tasks, milestoneAchievements, vaccineLogs });
  useEffect(() => {
    latestDataRef.current = { activeBaby, logs, tasks, milestoneAchievements, vaccineLogs };
  });

  useEffect(() => {
    if (!activeBaby) return;
    const controller = new AbortController();

    registerWebMCPTool({
      name: "get_recent_logs",
      description: `Returns recent baby-care events logged for ${activeBaby.name}, most recent first.`,
      inputSchema: {
        type: "object",
        properties: {
          type: { type: "string", enum: WEBMCP_LOG_TYPES, description: "Optional filter to only return events of this type." },
          limit: { type: "number", description: "Max number of events to return. Defaults to 20, capped at 50." },
        },
      },
      annotations: { readOnlyHint: true },
      execute: (args) => {
        const { type, limit } = args as { type?: LogType; limit?: number };
        const cappedLimit = Math.min(Math.max(limit ?? 20, 1), 50);
        return latestDataRef.current.logs
          .filter((l) => !type || l.type === type)
          .slice(0, cappedLimit)
          .map((l) => ({ type: l.type, occurredAt: l.occurredAt, payload: l.payload, notes: l.notes }));
      },
    }, controller.signal);

    registerWebMCPTool({
      name: "get_tasks",
      description: `Returns tasks/reminders for ${activeBaby.name}, including their ids for use with complete_task/delete_task.`,
      inputSchema: {
        type: "object",
        properties: {
          status: { type: "string", enum: ["overdue", "due_soon", "upcoming", "all"], description: "Filter by due-date bucket. Defaults to 'all'." },
        },
      },
      annotations: { readOnlyHint: true },
      execute: (args) => {
        const { status } = args as { status?: "overdue" | "due_soon" | "upcoming" | "all" };
        const now = Date.now();
        const sorted = [...latestDataRef.current.tasks].sort(
          (a, b) => new Date(a.nextDueAt).getTime() - new Date(b.nextDueAt).getTime()
        );
        const filtered = sorted.filter((task) => {
          if (!status || status === "all") return true;
          const due = new Date(task.nextDueAt).getTime();
          if (status === "overdue") return due < now;
          if (status === "due_soon") return due >= now && due <= now + 3600000;
          return due > now + 3600000;
        });
        return filtered.map((task) => ({
          id: task.id, title: task.title, nextDueAt: task.nextDueAt, isRecurring: task.isRecurring,
        }));
      },
    }, controller.signal);

    registerWebMCPTool({
      name: "get_milestone_status",
      description: `Returns due/overdue vaccines and pending/recently-achieved developmental milestones near ${activeBaby.name}'s current age, for use with log_vaccine/unlog_vaccine/toggle_milestone.`,
      inputSchema: { type: "object", properties: {} },
      annotations: { readOnlyHint: true },
      execute: () => {
        const { activeBaby: baby, vaccineLogs: vLogs, milestoneAchievements: achievements } = latestDataRef.current;
        if (!baby) return { babyAgeWeeks: 0, pendingOrOverdueVaccines: [], milestones: [] };
        const currentAgeWeeks = calculateAgeWeeks(baby.birthDate);

        const pendingOrOverdueVaccines: { label: string; weekDue: number; status: "due" | "overdue" }[] = [];
        if (baby.countryCode) {
          const countryData = VACCINATION_DATA[baby.countryCode];
          for (const weekNum of getAllScheduleWeeks(baby.countryCode)) {
            for (const label of countryData.schedule[weekNum] ?? []) {
              const logged = vLogs.some((l) => (l.payload.vaccineIds ?? []).includes(label) && l.payload.vaccineWeek === weekNum);
              if (logged) continue;
              pendingOrOverdueVaccines.push({ label, weekDue: weekNum, status: weekNum <= currentAgeWeeks ? "overdue" : "due" });
            }
          }
        }

        const nearbyWeeks = richNorms.map((n) => n.ageWeeks).filter((w) => Math.abs(w - currentAgeWeeks) <= 8);
        const milestones = nearbyWeeks.flatMap((w) => {
          const achievedTexts = new Set(
            achievements.filter((m) => m.ageWeeks === w).map((m) => m.milestoneText)
          );
          return getNormForWeek(w).milestones.map((text) => ({
            weekNum: w, text, achieved: achievedTexts.has(text),
          }));
        });

        return { babyAgeWeeks: currentAgeWeeks, pendingOrOverdueVaccines, milestones };
      },
    }, controller.signal);

    return () => controller.abort();
  }, [activeBaby]);

  // ── Auth helpers ────────────────────────────────────────────────────────────
  async function backfillCollaboratorEmails(email: string) {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const owned = babies.filter((b) => b.ownerUid === uid);
    await Promise.all(owned.map((b) =>
      updateDoc(doc(db, "babies", b.id), { collaboratorEmails: [email.toLowerCase()] })
    ));
  }

  async function handleLinkGoogle() {
    if (!auth.currentUser) return;
    setAuthError(null);
    setAuthBusy(true);
    try {
      const cred = await linkWithPopup(auth.currentUser, provider as GoogleAuthProvider, browserPopupRedirectResolver);
      reportAccountSignup("google");
      if (cred.user.email) await backfillCollaboratorEmails(cred.user.email);
      setAuthScreen("none");
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? "";
      setAuthError(getAuthErrorMessage(code, t));
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleLinkEmailPassword(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!auth.currentUser) return;
    setAuthError(null);
    setAuthBusy(true);
    try {
      const emailCred = EmailAuthProvider.credential(authEmail, authPassword);
      const cred = await linkWithCredential(auth.currentUser, emailCred);
      reportAccountSignup("password");
      try { await sendEmailVerification(cred.user); } catch { /* non-fatal */ }
      if (cred.user.email) await backfillCollaboratorEmails(cred.user.email);
      setAuthScreen("none");
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? "";
      setAuthError(getAuthErrorMessage(code, t));
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleSignInGoogle() {
    setAuthError(null);
    setAuthBusy(true);
    try {
      await signInWithPopup(auth, provider as GoogleAuthProvider, browserPopupRedirectResolver);
      setAuthScreen("none");
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? "";
      setAuthError(getAuthErrorMessage(code, t));
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleSignInEmailPassword(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setAuthError(null);
    setResetSent(false);
    setAuthBusy(true);
    try {
      await signInWithEmailAndPassword(auth, authEmail, authPassword);
      setAuthScreen("none");
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? "";
      setAuthError(getAuthErrorMessage(code, t));
    } finally {
      setAuthBusy(false);
    }
  }

  async function handleResendVerification() {
    if (!auth.currentUser) return;
    setVerifyResent(false);
    setVerifyStillUnverified(false);
    try {
      await sendEmailVerification(auth.currentUser);
      setVerifyResent(true);
    } catch { /* non-fatal */ }
  }

  async function handleCheckVerification() {
    if (!auth.currentUser) return;
    setVerifyChecking(true);
    setVerifyResent(false);
    setVerifyStillUnverified(false);
    try {
      await auth.currentUser.reload();
      await auth.currentUser.getIdToken(true);
      if (auth.currentUser.emailVerified) {
        setUser(auth.currentUser);
      } else {
        setVerifyStillUnverified(true);
      }
    } finally {
      setVerifyChecking(false);
    }
  }

  async function handlePasswordReset() {
    if (!authEmail) { setAuthError(t("auth.errorEmailRequired")); return; }
    setAuthError(null);
    setResetSent(false);
    try {
      await sendPasswordResetEmail(auth, authEmail);
      setResetSent(true);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? "";
      setAuthError(getAuthErrorMessage(code, t));
    }
  }

  // ── Auth guards ─────────────────────────────────────────────────────────────
  if (loading) return <main className="screen screen--loading"><div className="spinner" /></main>;

  if (!user) {
    return (
      <main className="screen">
        <section className="panel auth-panel">
          <h1 className="auth-emoji">👶</h1>
          <h2 className="auth-title">{t("auth.title")}</h2>
          <p className="auth-subtitle">{t("auth.initErrorBody")}</p>
          {authInitError && <p className="auth-error">{t("auth.errorGeneric")}</p>}
          <button type="button" className="primary-btn" onClick={() => window.location.reload()}>
            {t("auth.retry")}
          </button>
        </section>
      </main>
    );
  }

  const needsVerification = !user.isAnonymous
    && !user.emailVerified
    && user.providerData.some((p) => p.providerId === "password");

  if (authScreen === "register") {
    return (
      <RegisterScreen
        authBusy={authBusy}
        authEmail={authEmail}
        authPassword={authPassword}
        authError={authError}
        onEmailChange={(value) => { setAuthEmail(value); setAuthError(null); }}
        onPasswordChange={(value) => { setAuthPassword(value); setAuthError(null); }}
        onGoogleClick={handleLinkGoogle}
        onSubmit={handleLinkEmailPassword}
        onSwitchToSignIn={() => { setAuthScreen("signin"); setAuthError(null); }}
        onCancel={() => { setAuthScreen("none"); setAuthError(null); }}
      />
    );
  }

  if (authScreen === "signin") {
    const hasAnonData = user.isAnonymous && babies.length > 0;
    return (
      <SignInScreen
        hasAnonData={hasAnonData}
        authBusy={authBusy}
        authEmail={authEmail}
        authPassword={authPassword}
        authError={authError}
        resetSent={resetSent}
        language={userSettings.language ?? "en"}
        onEmailChange={(value) => { setAuthEmail(value); setAuthError(null); setResetSent(false); }}
        onPasswordChange={(value) => { setAuthPassword(value); setAuthError(null); }}
        onGoogleClick={handleSignInGoogle}
        onSubmit={handleSignInEmailPassword}
        onForgotPassword={handlePasswordReset}
        onLanguageChange={(lang) => saveUserSettings({ language: lang })}
        onCancel={() => { setAuthScreen("none"); setAuthError(null); setResetSent(false); }}
      />
    );
  }

  if (!babiesReady || (!activeBaby && babies.length > 0)) {
    return <main className="screen screen--loading"><div className="spinner" /></main>;
  }

  if (!activeBaby) {
    const onboardingMissingFields = [
      !newBabyName && t("onboarding.babyNamePlaceholder"),
      !newBabyBirthDate && t("onboarding.dateOfBirth"),
    ].filter((v): v is string => !!v);
    return (
      <OnboardingView
        newBabyName={newBabyName}
        setNewBabyName={setNewBabyName}
        newBabyBirthDate={newBabyBirthDate}
        setNewBabyBirthDate={setNewBabyBirthDate}
        newBabySex={newBabySex}
        setNewBabySex={setNewBabySex}
        newBabyFeedingMode={newBabyFeedingMode}
        setNewBabyFeedingMode={setNewBabyFeedingMode}
        language={userSettings.language ?? "en"}
        saveUserSettings={saveUserSettings}
        onboardingSubmitAttempted={onboardingSubmitAttempted}
        onboardingMissingFields={onboardingMissingFields}
        onSubmit={() => {
          if (onboardingMissingFields.length > 0) { setOnboardingSubmitAttempted(true); return; }
          setOnboardingSubmitAttempted(false);
          onAddBaby();
        }}
        isAnonymous={user.isAnonymous}
        onRequestSignIn={() => setAuthScreen("signin")}
        onSignOut={() => signOut(auth)}
      />
    );
  }

  // ── Main app ────────────────────────────────────────────────────────────────
  const ageWeeks = calculateAgeWeeks(activeBaby.birthDate);
  const unitSystem: UnitSystem = userSettings.unitSystem ?? "metric";
  const hasSufficientData = logs.length >= 5;
  const isNewProfile = logsLoaded && logs.length === 0;
  const upcomingLeap: DevelopmentalLeapResult = detectDevelopmentalLeap(ageWeeks, norm);

  return (
    <main className="screen">
      <section className="panel app-panel">
        <header className="app-header">
          <h2>{activeBaby.name}</h2>
          <span className="app-header-age">{formatBabyAge(activeBaby.birthDate)}</span>
        </header>

        {activeSleepSession && (
          <SleepTimerBanner
            startedAt={activeSleepSession.startedAt}
            onOpen={() => openFormOverlay("sleep")}
          />
        )}

        <Suspense fallback={<ViewLoadingFallback />}>
        {view === "home" && (
          <HomeView
            activeBaby={activeBaby}
            logs={logs}
            logsLoaded={logsLoaded}
            norm={norm}
            tasks={tasks}
            userSettings={userSettings}
            insight={insight}
            todaysStats={todaysStats}
            vaccineLogs={vaccineLogs}
            ageWeeks={ageWeeks}
            latestWeightKg={latestWeightKg}
            effectiveFeedingTargetMl={effectiveFeedingTargetMl}
            effectiveFeedingMinMl={effectiveFeedingMinMl}
            growthVelocity={growthVelocity}
            aiResponse={aiResponse}
            copilotLoading={copilotLoading}
            copilotGeneratedAt={copilotGeneratedAt}
            hasSufficientData={hasSufficientData}
            aiEnabled={userSettings.aiEnabled ?? false}
            upcomingLeap={upcomingLeap}
            setView={setView}
            setSelectedLog={setSelectedLog}
            setReferenceModal={setReferenceModal}
            isAnonymous={!!user.isAnonymous}
            onRequestRegister={() => setAuthScreen("register")}
            activeSleepSession={activeSleepSession}
            signedInAt={user.metadata.creationTime ?? null}
            onAddLog={() => { reportLogActivityClick(); openFormOverlay(); }}
          />
        )}

        {view === "logging" && (
          <LoggingView
            activeBaby={activeBaby}
            logs={logs}
            stats={stats}
            userSettings={userSettings}
            saveUserSettings={saveUserSettings}
            selectedLog={selectedLog}
            setSelectedLog={setSelectedLog}
            onDeleteLog={handleDeleteLog}
            effectiveFeedingTargetMl={effectiveFeedingTargetMl}
            activeSleepSession={activeSleepSession}
            logSubTab={logSubTab}
            setLogSubTab={setLogSubTab}
          />
        )}

        {view === "import" && user && (
          <ImportView
            activeBaby={activeBaby}
            user={user}
            norm={norm}
            growthVelocity={growthVelocity}
            setView={setView}
          />
        )}

        {view === "analytics" && (
          <AnalyticsView
            activeBaby={activeBaby}
            logs={logs}
            stats={stats}
            norm={norm}
            userSettings={userSettings}
            saveUserSettings={saveUserSettings}
            ageWeeks={ageWeeks}
            effectiveFeedingTargetMl={effectiveFeedingTargetMl}
            setReferenceModal={setReferenceModal}
          />
        )}

        {view === "milestones" && user && (
          <MilestonesView
            activeBaby={activeBaby}
            logs={logs}
            norm={norm}
            milestoneAchievements={milestoneAchievements}
            ageWeeks={ageWeeks}
            vaccineLogs={vaccineLogs}
            user={user}
            setView={setView}
          />
        )}

        {view === "tasks" && user && (
          <TasksView
            activeBaby={activeBaby}
            tasks={tasks}
            userSettings={userSettings}
            user={user}
          />
        )}

        {view === "settings" && user && (
          <SettingsView
            activeBaby={activeBaby}
            babies={babies}
            user={user}
            logs={logs}
            stats={stats}
            userSettings={userSettings}
            saveUserSettings={saveUserSettings}
            setActiveBaby={setActiveBaby}
            setView={setView}
            needsVerification={needsVerification}
            onRequestRegister={() => setAuthScreen("register")}
            onRequestSignIn={() => setAuthScreen("signin")}
            onResendVerification={handleResendVerification}
            verifyResent={verifyResent}
            onCheckVerification={handleCheckVerification}
            verifyChecking={verifyChecking}
            verifyStillUnverified={verifyStillUnverified}
          />
        )}

        {view === "about" && (
          <AboutView onBack={() => setView("settings")} />
        )}
        </Suspense>

        {view !== "settings" && view !== "import" && view !== "about" && !(view === "home" && isNewProfile) && (
          <button
            className={`fab-btn${isNewProfile ? " fab-btn--attention" : ""}`}
            aria-label="Log activity"
            onClick={() => { reportLogActivityClick(); openFormOverlay(); }}
          >
            +
          </button>
        )}

        <footer className="disclaimer">{t("disclaimer")}</footer>

        <nav className="bottom-nav" aria-label={t("nav.primary")}>
          {([
            ["home",       "home",  Home],
            ["logging",    "logs",  BookOpen],
            ["analytics",  "analytics", BarChart2],
            ["milestones", "milestones", Target],
            ["tasks",      "tasks", ListChecks],
            ["settings",   "settings", Settings],
          ] as [View, string, ComponentType<{ size?: number; strokeWidth?: number }>][]).map(([v, labelKey, Icon]) => {
            const isActive = view === v || (v === "settings" && (view === "import" || view === "about"));
            const badge = v === "tasks" && tasks.filter((t) => new Date(t.nextDueAt).getTime() < Date.now()).length;
            return (
              <button
                key={v}
                className={`nav-tab${isActive ? " active" : ""}`}
                onClick={() => { setView(v); setSelectedLog(null); if (formOverlayOpen) closeFormOverlay(); }}
                aria-label={t(`nav.${labelKey}`)}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="nav-icon-wrap">
                  <Icon size={22} strokeWidth={1.75} />
                  {!!badge && <span className="nav-badge">{badge}</span>}
                </span>
              </button>
            );
          })}
        </nav>
      </section>

      {formOverlayOpen && user && (
        <Suspense
          fallback={
            <div className="form-overlay-backdrop">
              <div className="screen--loading" role="status" aria-live="polite" aria-label={t("common.loading")}>
                <div className="spinner" />
              </div>
            </div>
          }
        >
          <FormOverlay
            activeBaby={activeBaby}
            user={user}
            logs={logs}
            userSettings={userSettings}
            norm={norm}
            insight={insight}
            activeSleepSession={activeSleepSession}
            autoOpenType={formOverlayAutoType}
            onClose={closeFormOverlay}
          />
        </Suspense>
      )}

      {referenceModal !== null && activeBaby && norm && (
        <ReferenceModal
          kind={referenceModal}
          activeBaby={activeBaby}
          norm={norm}
          logs={logs}
          todaysStats={todaysStats}
          effectiveFeedingMinMl={effectiveFeedingMinMl}
          effectiveFeedingTargetMl={effectiveFeedingTargetMl}
          growthVelocity={growthVelocity}
          ageWeeks={ageWeeks}
          unitSystem={unitSystem}
          onClose={() => setReferenceModal(null)}
        />
      )}
    </main>
  );
}
